import React, { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link2, Loader2, Upload, X } from "lucide-react";

const UNITS = ["each", "m²", "lm", "m", "set", "roll", "sheet", "kg"];
const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const EMPTY = {
  name: "", category: "", supplier: "", brand: "", code: "", colour: "", size: "",
  unit: "each", cost_ex_gst: "", lead_time_weeks: "", supplier_url: "", spec_sheet_url: "",
  spec_sheet_path: "", notes: "", tags: "",
};

export default function ProductForm({ open, onOpenChange, product, categories, suppliers, onSaved, initial }) {
  const isEdit = !!product;
  const [form, setForm] = useState(EMPTY);
  const [prefilled, setPrefilled] = useState({});
  const [fetchUrl, setFetchUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchMsg, setFetchMsg] = useState("");
  const [fetchThin, setFetchThin] = useState(false);
  const [fetchedImage, setFetchedImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingSpec, setPendingSpec] = useState(null);
  const imgInput = useRef(null);

  useEffect(() => {
    if (open) {
      setPrefilled({}); setFetchMsg(""); setFetchThin(false);
      setPendingImages([]); setPendingSpec(null);
      if (product) {
        setFetchUrl(product.supplier_url || ""); setFetchedImage("");
        setForm({
          name: product.name || "", category: product.category || "", supplier: product.supplier || "",
          brand: product.brand || "", code: product.code || "", colour: product.colour || "",
          size: product.size || "", unit: product.unit || "each",
          cost_ex_gst: product.cost_ex_gst ?? "", lead_time_weeks: product.lead_time_weeks ?? "",
          supplier_url: product.supplier_url || "", spec_sheet_url: product.spec_sheet_url || "",
          spec_sheet_path: product.spec_sheet_path || "", notes: product.notes || "",
          tags: (product.tags || []).join(", "),
        });
      } else if (initial) {
        const pf = {};
        ["name", "brand", "code"].forEach((k) => { if (initial[k]) pf[k] = true; });
        if (initial.price != null && initial.price !== "") pf.cost_ex_gst = true;
        setPrefilled(pf);
        setFetchUrl(initial.url || "");
        setFetchedImage(initial.image || "");
        setForm({
          ...EMPTY, name: initial.name || "", brand: initial.brand || "", code: initial.code || "",
          cost_ex_gst: initial.price ?? "", supplier_url: initial.url || "",
          notes: initial.notes || initial.description || "",
        });
        setFetchMsg(initial.url ? "Clipped from the supplier page — check the details, then save." : "");
      } else {
        setFetchUrl(""); setFetchedImage(""); setForm(EMPTY);
      }
    }
  }, [open, product, initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const doFetch = async () => {
    const u = fetchUrl.trim();
    if (!u) { toast.error("Paste a supplier URL first"); return; }
    setFetching(true); setFetchMsg(""); setFetchThin(false);
    set({ supplier_url: u });  // always save the supplier URL
    try {
      const { data } = await api.post("/library/fetch-url", { url: u });
      if (!data.ok) { setFetchMsg(data.message || "Couldn't read that page."); return; }
      if (data.is_pdf) { set({ spec_sheet_url: u, supplier_url: u }); setFetchMsg("That link is a PDF — saved as the spec sheet."); return; }
      const next = {}, pf = {};
      [["name", data.name], ["brand", data.brand], ["code", data.code],
       ["cost_ex_gst", data.price], ["notes", data.description]].forEach(([field, val]) => {
        if (val != null && val !== "" && !String(form[field] || "").trim()) { next[field] = val; pf[field] = true; }
      });
      setForm((f) => ({ ...f, ...next }));
      setPrefilled((p) => ({ ...p, ...pf }));
      if (data.image_url && !(product?.images || []).length) setFetchedImage(data.image_url);
      if (data.thin) {
        setFetchThin(true);
        setFetchMsg("Only the page title could be read — this site loads its product details with JavaScript. Use the Clip to TWP button on the supplier page, or fill in by hand.");
      } else {
        setFetchMsg(`Fetched from ${data.host || "page"} — check the details, then save.`);
      }
    } catch (e) { setFetchMsg("Couldn't read that page — enter the details by hand."); }
    finally { setFetching(false); }
  };

  const persistExtras = async (pid) => {
    if (fetchedImage) {
      try { await api.post(`/library/products/${pid}/images/from-url`, { url: fetchedImage }); }
      catch { toast.error("Couldn't save the fetched image"); }
    }
    for (const f of pendingImages) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/library/products/${pid}/images`, fd); } catch { toast.error(`Image ${f.name} failed`); }
    }
    if (pendingSpec) {
      const fd = new FormData(); fd.append("file", pendingSpec);
      try { await api.post(`/library/products/${pid}/spec-sheet`, fd); } catch { toast.error("Spec sheet upload failed"); }
    }
  };

  const save = async (addAnother) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.category) { toast.error("Category is required"); return; }
    if (form.cost_ex_gst !== "" && (isNaN(Number(form.cost_ex_gst)) || Number(form.cost_ex_gst) < 0)) {
      toast.error("Cost must be a number ≥ 0"); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      cost_ex_gst: form.cost_ex_gst === "" ? null : Number(form.cost_ex_gst),
      lead_time_weeks: form.lead_time_weeks === "" ? null : Number(form.lead_time_weeks),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      let pid;
      if (isEdit) { await api.patch(`/library/products/${product.product_id}`, payload); pid = product.product_id; }
      else { const { data } = await api.post("/library/products", payload); pid = data.product_id; }
      await persistExtras(pid);
      toast.success(isEdit ? "Product updated" : "Product added");
      onSaved();
      if (addAnother && !isEdit) {
        setForm(EMPTY); setPrefilled({}); setFetchUrl(""); setFetchMsg(""); setFetchThin(false);
        setFetchedImage(""); setPendingImages([]); setPendingSpec(null);
      } else { onOpenChange(false); }
    } catch (e) { toast.error(apiErr(e, "Could not save product")); }
    finally { setSaving(false); }
  };

  const L = "text-xs font-semibold uppercase tracking-wider text-zinc-500";
  const field = (key, label, props = {}) => (
    <div>
      <label className={L}>{label}</label>
      <Input data-testid={`product-${key}`} value={form[key]} onChange={(e) => { set({ [key]: e.target.value }); setPrefilled((p) => ({ ...p, [key]: false })); }}
        className={`mt-1.5 rounded-sm ${prefilled[key] ? "bg-emerald-50" : ""}`} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl w-[calc(100vw-2rem)] sm:w-full max-h-[92vh] overflow-y-auto" data-testid="product-form">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>

        {/* Fetch from URL */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
          <label className={L}>Supplier URL</label>
          <div className="flex gap-2 mt-1.5">
            <div className="relative flex-1">
              <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input data-testid="product-fetch-url" value={fetchUrl} onChange={(e) => setFetchUrl(e.target.value)}
                placeholder="https://supplier.com/product…" className="pl-8 rounded-sm" />
            </div>
            <Button type="button" onClick={doFetch} disabled={fetching} variant="outline" className="rounded-sm gap-1.5 shrink-0" data-testid="product-fetch-btn">
              {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Fetch details
            </Button>
          </div>
          {fetchMsg && <p className={`text-xs mt-1.5 ${fetchThin ? "text-amber-600" : "text-zinc-500"}`} data-testid="product-fetch-msg">{fetchMsg}</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-1">
          {field("name", "Name *")}
          <div>
            <label className={L}>Category *</label>
            <Select value={form.category} onValueChange={(v) => set({ category: v })}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="product-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={L}>Supplier</label>
            <Input data-testid="product-supplier" value={form.supplier} list="supplier-list" onChange={(e) => set({ supplier: e.target.value })} className="mt-1.5 rounded-sm" />
            <datalist id="supplier-list">{(suppliers || []).map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          {field("brand", "Brand")}
          {field("code", "Code")}
          {field("colour", "Colour")}
          {field("size", "Size")}
          <div>
            <label className={L}>Unit</label>
            <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="product-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("cost_ex_gst", "Cost ex GST (AUD)", { type: "number", min: 0, step: "0.01" })}
          {field("lead_time_weeks", "Lead time (weeks)", { type: "number", min: 0, step: "0.5" })}
          {field("tags", "Tags (comma separated)")}
        </div>

        {/* Images */}
        <div className="mt-1">
          <label className={L}>Images</label>
          <input ref={imgInput} type="file" accept="image/*" capture multiple className="hidden" data-testid="product-images-input"
            onChange={(e) => { setPendingImages((p) => [...p, ...Array.from(e.target.files)]); e.target.value = ""; }} />
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {(product?.images || []).map((im) => (
              <img key={im.file_id} src={`${FILE_BASE}/${im.path}`} alt={im.name} className="h-14 w-14 object-cover rounded-sm border border-zinc-200" />
            ))}
            {fetchedImage && (
              <span className="relative" data-testid="fetched-image-thumb">
                <img src={fetchedImage} alt="fetched" className="h-14 w-14 object-cover rounded-sm border-2 border-emerald-400" />
                <button onClick={() => setFetchedImage("")} className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
              </span>
            )}
            {pendingImages.map((f, i) => (
              <span key={`${f.name}-${f.size}-${f.lastModified}`} className="inline-flex items-center gap-1 text-xs bg-zinc-100 rounded-sm px-2 py-1" data-testid={`pending-image-${i}`}>
                {f.name.slice(0, 18)}<button onClick={() => setPendingImages((p) => p.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
              </span>
            ))}
            <Button type="button" variant="outline" className="rounded-sm gap-1.5 h-9" onClick={() => imgInput.current?.click()} data-testid="product-add-image">
              <Upload className="h-4 w-4" /> Add photo
            </Button>
          </div>
        </div>

        {/* Spec sheet */}
        <div className="grid sm:grid-cols-2 gap-3 mt-1 items-end">
          {field("spec_sheet_url", "Spec sheet URL")}
          <div>
            <label className={L}>…or upload PDF</label>
            <input type="file" accept="application/pdf,image/*" data-testid="product-spec-input"
              onChange={(e) => setPendingSpec(e.target.files[0] || null)}
              className="mt-1.5 block w-full text-xs file:mr-3 file:rounded-sm file:border file:border-zinc-200 file:bg-white file:px-3 file:py-1.5 file:text-xs" />
            {pendingSpec && <p className="text-[11px] text-zinc-500 mt-1">{pendingSpec.name}</p>}
          </div>
        </div>

        <div className="mt-1">
          <label className={L}>Notes</label>
          <Textarea data-testid="product-notes" value={form.notes} rows={2} onChange={(e) => set({ notes: e.target.value })} className="mt-1.5 rounded-sm" />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="product-cancel">Cancel</Button>
          {!isEdit && <Button variant="outline" className="rounded-sm" onClick={() => save(true)} disabled={saving} data-testid="product-save-another">Save &amp; add another</Button>}
          <Button onClick={() => save(false)} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="product-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
